/** The members grid's toolbar pieces: the save indicator and the column chooser. */

import React, { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tabs,
  TabsCount,
  TabsList,
  TabsTrigger,
} from '@roger/ui-components';
import { Check, ChevronDown, Loader2 } from 'lucide-react';

/**
 * "Saving…" while any write is in flight, then "Saved" for a moment. Waits
 * before saying "Saving…" so a quick save does not flicker.
 */
export const SaveStatus: React.FC<{
  saving: boolean;
  /** The last save failed: its own message has said so, so no "Saved". */
  failed: boolean;
}> = ({ saving, failed }) => {
  const [shown, setShown] = useState<'saving' | 'saved' | null>(null);
  const wasSaving = useRef(false);
  useEffect(() => {
    if (saving) {
      wasSaving.current = true;
      const timer = setTimeout(() => setShown('saving'), 300);
      return () => clearTimeout(timer);
    }
    if (!wasSaving.current) return;
    // Every save ends with "Saved", even one too quick to say "Saving…",
    // unless it failed.
    wasSaving.current = false;
    if (failed) {
      setShown(null);
      return;
    }
    setShown('saved');
    const timer = setTimeout(() => setShown(null), 1500);
    return () => clearTimeout(timer);
  }, [saving, failed]);
  // The announcer is always in the page, so screen readers hear each change;
  // the visible text takes no room until it has something to say.
  return (
    <>
      <span aria-live='polite' className='sr-only'>
        {shown === 'saving' ? 'Saving…' : shown === 'saved' ? 'Saved' : ''}
      </span>
      <span
        aria-hidden
        className='flex items-center gap-1.5 text-xs text-muted-foreground empty:hidden'
      >
        {shown === 'saving' && (
          <>
            <Loader2 className='h-3.5 w-3.5 animate-spin' />
            Saving…
          </>
        )}
        {shown === 'saved' && (
          <>
            <Check className='h-3.5 w-3.5 text-emerald-600' />
            Saved
          </>
        )}
      </span>
    </>
  );
};

/**
 * A chip that opens a checklist: here, the columns to hide. Drawn as the
 * enrollment filters' chip, with a last row that unticks everything.
 */
export const ColumnChooser: React.FC<{
  label: React.ReactNode;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (value: string[]) => void;
  /** Says what ticking an option does. */
  heading: string;
  clearLabel: string;
}> = ({ label, options, selected, onChange, heading, clearLabel }) => {
  const toggle = (value: string) =>
    onChange(
      selected.includes(value)
        ? selected.filter(v => v !== value)
        : [...selected, value]
    );
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant='outline' className='whitespace-nowrap'>
          {label}
          {selected.length > 0 && (
            <Badge variant='destructive' className='ml-2'>
              {selected.length}
            </Badge>
          )}
          <ChevronDown className='ml-1 h-3 w-3' />
        </Button>
      </PopoverTrigger>
      <PopoverContent align='start' className='w-64 space-y-1'>
        <p className='px-1 pb-1 text-sm text-muted-foreground'>{heading}</p>
        {options.map(option => (
          <label
            key={option.value}
            className='flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-muted'
          >
            <Checkbox
              checked={selected.includes(option.value)}
              onCheckedChange={() => toggle(option.value)}
              checkboxSize='sm'
            />
            {option.label}
          </label>
        ))}
        {/* Always there, greyed when there is nothing to undo, so the list
            does not shift under the pointer. */}
        <button
          type='button'
          disabled={selected.length === 0}
          className='mt-1 w-full border-t px-1 pt-1.5 text-left text-xs text-primary hover:underline disabled:cursor-default disabled:text-muted-foreground/60 disabled:no-underline'
          onClick={() => onChange([])}
        >
          {clearLabel}
        </button>
      </PopoverContent>
    </Popover>
  );
};

/**
 * Segmented list picker for the members grid. Uses the shared Tabs control so
 * height, padding, and count pills match the other manager tab bars.
 */
export function ViewToggle<V extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: V; label: string; count: number }[];
  value: V;
  onChange: (value: V) => void;
}) {
  return (
    <Tabs
      value={value}
      onValueChange={next => onChange(next as V)}
      variant='purple'
      className='w-fit max-w-full'
    >
      <TabsList aria-label={label} className='max-sm:h-auto max-sm:flex-wrap'>
        {options.map(option => (
          <TabsTrigger key={option.value} value={option.value}>
            <span className='inline-flex items-center gap-x-1'>
              <span>{option.label}</span>
              <TabsCount label={option.label} value={String(option.count)} />
            </span>
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}
